// Sopwith 3D - ground targets, oxen, bird flocks, balloons and power-ups.
//
// Copyright (C) 1984-2000 David L. Clark
// Copyright (C) 2001-2026 Simon Howard and SDL Sopwith contributors
// Copyright (C) 2026 Sopwith 3D contributors
//
// This program is free software; you can redistribute it and/or modify it
// under the terms of the GNU General Public License as published by the Free
// Software Foundation; either version 2 of the License, or (at your option)
// any later version. This program is distributed WITHOUT ANY WARRANTY.
//
// Ported from SDL Sopwith swinit.c / swmove.c.

import { range } from './ai';
import { BIRDLIFE, FLOCKLIFE, MAX_Y, NUM_STRAY_BIRDS } from './constants';
import { clampMax, iabs, imax, imin, inRange, SIN } from './fixed';
import type { Game } from './game';
import { planeIsKilled } from './plane';
import { getSymset } from './symbols';
import { Faction, Obj, ObjState, ObjType, PlayMode, type OriginalOb } from './types';
import { initshot } from './weapons';

/**
 * How often each target type fires at enemy planes: 0 never, otherwise the
 * lower the value the more often. Indexed by TargetType.
 */
const TARGET_AGGRESSION = [2, 2, 2, 2, 5, 5, 0, 0, 2, 2, 2, 5, 5, 0, 0, 0, 0, 0, 0, 0];

/** The nearest enemy plane within firing range of a ground object, if any. */
function findEnemyPlane(g: Game, ob: Obj): Obj | null {
  for (let obp = g.world.objtop; obp; obp = obp.next) {
    if (obp.type !== ObjType.PLANE || obp.faction === ob.faction) {
      continue;
    }
    // In single player only the human is a target.
    if (g.playmode !== PlayMode.ASYNCH && obp.faction !== Faction.PLAYER1) {
      continue;
    }
    if (planeIsKilled(obp.state)) {
      continue;
    }
    const r = range(ob.x, ob.y, obp.x, obp.y);
    if (inRange(1, r, g.targrnge - 1)) {
      return obp;
    }
  }
  return null;
}

const xy = { x: 0, y: 0 };

// ---------------------------------------------------------------------------
// Factions

/**
 * Map a level object's faction onto the two sides of a single-player game
 * and count it as a target for that side.
 */
export function addPlayerTarget(g: Game, ob: Obj, orig: OriginalOb): void {
  switch (orig.faction) {
    case Faction.PLAYER1:
    case Faction.PLAYER5:
    case Faction.PLAYER7:
      ob.faction = Faction.PLAYER1;
      break;
    case Faction.PLAYER2:
    case Faction.PLAYER4:
    case Faction.PLAYER6:
    case Faction.PLAYER8:
      ob.faction = Faction.PLAYER2;
      break;
    case Faction.PLAYER3:
      ob.faction = g.playmode === PlayMode.ASYNCH ? Faction.PLAYER1 : Faction.PLAYER2;
      break;
    default:
      ob.faction = Faction.NONE;
      break;
  }
  g.numtarg[ob.faction]++;
}

// ---------------------------------------------------------------------------
// Ground targets

/** Level the ground under a building; returns the new height. */
function flatten(g: Game, minx: number, maxx: number, headroom: number): number {
  let minh = 999;
  let maxh = 0;
  for (let x = minx; x <= maxx; x++) {
    minh = imin(minh, g.ground[x]);
    maxh = imax(maxh, g.ground[x]);
  }
  let aveh = ((minh + maxh) / 2) | 0;
  aveh = clampMax(aveh, MAX_Y - headroom - 1);
  for (let x = minx; x <= maxx; x++) {
    g.ground[x] = aveh;
  }
  return aveh;
}

export function inittarget(g: Game, orig: OriginalOb): Obj {
  const ob = g.world.allocobj();
  ob.symbol = getSymset(g.symbols, 'swtrgsym', orig.orient).sym[0];
  ob.x = orig.x;
  ob.y = flatten(g, ob.x, ob.x + ob.symbol.w - 1, ob.symbol.h) + ob.symbol.h;
  ob.dx = ob.dy = ob.lx = ob.ly = ob.ldx = ob.ldy = ob.angle = ob.hitcount = 0;
  ob.type = ObjType.TARGET;
  ob.state = ObjState.STANDING;
  ob.orient = orig.orient;
  addPlayerTarget(g, ob, orig);
  ob.clr = ob.faction;
  ob.movef = movetarg;
  ob.onmap = true;
  return ob;
}

export function movetarg(g: Game, ob: Obj): boolean {
  const oldsym = ob.symbol!;
  const transform = ob.originalOb!.transform;
  ob.firing = null;

  // From level 1 the buildings shoot back; from level 2 on every tick.
  const aggression = TARGET_AGGRESSION[ob.orient] ?? 0;
  if (
    ob.state === ObjState.STANDING &&
    g.gamenum > 0 &&
    aggression > 0 &&
    (g.gamenum > 1 || g.countmove % aggression === aggression - 1)
  ) {
    const plane = findEnemyPlane(g, ob);
    if (plane) {
      initshot(g, ob, plane);
      ob.firing = plane;
    }
  }

  ob.hitcount = imax(ob.hitcount - 1, 0);

  if (ob.state === ObjState.STANDING) {
    ob.symbol = getSymset(g.symbols, 'swtrgsym', ob.orient).sym[transform];
  } else {
    ob.symbol = getSymset(g.symbols, 'swhtrsym', ob.orient).sym[transform];
  }

  // A destroyed sprite may differ in size: stay centred and on the ground.
  ob.x += ((oldsym.w - ob.symbol.w) / 2) | 0;
  ob.y -= oldsym.h - ob.symbol.h;
  return true;
}

// ---------------------------------------------------------------------------
// Power-ups

export function initpowerup(g: Game, orig: OriginalOb): Obj {
  const ob = g.world.allocobj();
  ob.symbol = getSymset(g.symbols, 'swpowerupsym', orig.orient).sym[0];
  ob.x = orig.x;
  ob.y = flatten(g, ob.x, ob.x + ob.symbol.w - 1, ob.symbol.h) + ob.symbol.h;
  ob.dx = ob.dy = ob.lx = ob.ly = ob.ldx = ob.ldy = ob.angle = ob.hitcount = 0;
  ob.type = ObjType.POWERUP;
  ob.state = ObjState.STANDING;
  ob.orient = orig.orient;
  ob.faction = Faction.NONE;
  ob.clr = 1;
  ob.movef = movepowerup;
  ob.onmap = true;
  return ob;
}

export function movepowerup(g: Game, ob: Obj): boolean {
  const transform = ob.originalOb!.transform;
  const name = ob.state === ObjState.STANDING ? 'swpowerupsym' : 'swpowercollsym';
  ob.symbol = getSymset(g.symbols, name, ob.orient).sym[transform];
  return true;
}

// ---------------------------------------------------------------------------
// Birds

export function initflock(g: Game, orig: OriginalOb): Obj | null {
  if (g.playmode === PlayMode.NOVICE || !g.conf.animals) {
    return null;
  }
  const ob = g.world.allocobj();
  ob.type = ObjType.FLOCK;
  ob.state = ObjState.FLYING;
  ob.x = orig.x;
  ob.y = MAX_Y - 1;
  ob.dx = ob.x < g.level.maxX / 2 ? 2 : -2;
  ob.dy = ob.lx = ob.ly = ob.ldx = ob.ldy = 0;
  ob.orient = 0;
  ob.life = FLOCKLIFE;
  ob.faction = Faction.NONE;
  ob.symbol = getSymset(g.symbols, 'swflksym', 0).sym[0];
  ob.movef = moveflck;
  ob.clr = 1;
  ob.onmap = true;
  for (let j = 0; j < NUM_STRAY_BIRDS; j++) {
    initbird(g, ob, 1);
  }
  return ob;
}

const IBX = [8, 3, 0, 6, 7, 14, 10, 12];
const IBY = [16, 1, 8, 3, 12, 10, 7, 14];
const IBDX = [-2, 2, -3, 3, -1, 1, 0, 0];
const IBDY = [-1, -2, -1, -2, -1, -2, -1, -2];

/** Release bird `i` (0..7) from a flock. */
export function initbird(g: Game, flock: Obj, i: number): void {
  const ob = g.world.allocobj();
  ob.type = ObjType.BIRD;
  ob.x = flock.x + IBX[i];
  ob.y = flock.y - IBY[i];
  ob.dx = IBDX[i];
  ob.dy = IBDY[i];
  ob.orient = ob.lx = ob.ly = ob.ldx = ob.ldy = 0;
  ob.life = BIRDLIFE;
  ob.faction = flock.faction;
  ob.symbol = getSymset(g.symbols, 'swbrdsym', 0).sym[0];
  ob.movef = movebird;
  ob.clr = flock.clr;
  g.world.insertx(ob, g.world.topobj);
}

export function moveflck(g: Game, ob: Obj): boolean {
  if (ob.life === -1) {
    g.world.deallobj(ob);
    return false;
  }
  ob.life--;
  if (ob.life <= 0) {
    ob.orient = ob.orient ? 0 : 1;
    ob.life = FLOCKLIFE;
  }
  // Flocks patrol back and forth within their territory.
  const orig = ob.originalOb!;
  if (ob.x < orig.territoryL) {
    ob.dx = iabs(ob.dx);
  } else if (ob.x > orig.territoryR) {
    ob.dx = -iabs(ob.dx);
  }
  g.world.movexy(ob, g.level.maxX, xy);
  ob.symbol = getSymset(g.symbols, 'swflksym', ob.orient).sym[0];
  return true;
}

/** Is there a wall (ground more than 10 above the bird) within 20 columns in `direction`? */
function checkwall(g: Game, ob: Obj, direction: number): boolean {
  let x = ob.x;
  for (let cnt = 0; cnt < 20; cnt++) {
    if (!inRange(0, x, g.level.maxX - 1)) {
      return true;
    }
    if (g.ground[x] > ob.y + 10) {
      return true;
    }
    x += direction < 0 ? -1 : 1;
  }
  return false;
}

export function movebird(g: Game, ob: Obj): boolean {
  if (ob.life === -1) {
    g.world.deallobj(ob);
    return false;
  } else if (ob.life === -2) {
    // Bounced off the ground or a wall: pick a new heading.
    ob.dy = -ob.dy;
    ob.dx = (g.countmove & 7) - 4;
    if (checkwall(g, ob, ob.dx)) {
      ob.dx = -ob.dx;
    }
    ob.life = BIRDLIFE;
  } else {
    ob.life--;
    if (ob.life <= 0) {
      ob.orient = ob.orient ? 0 : 1;
      ob.life = BIRDLIFE;
    }
  }

  g.world.movexy(ob, g.level.maxX, xy);
  ob.symbol = getSymset(g.symbols, 'swbrdsym', ob.orient).sym[0];
  if (!inRange(0, xy.x, g.level.maxX - 1) || !inRange(g.ground[xy.x] + 1, xy.y, MAX_Y - 1)) {
    ob.y -= ob.dy;
    ob.life = -2;
    return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Balloons and oxen

export function initballoon(g: Game, orig: OriginalOb): Obj {
  const ob = g.world.allocobj();
  ob.type = ObjType.BALLOON;
  ob.state = ObjState.FLYING;
  ob.life = 1;
  ob.x = orig.x;
  ob.y = MAX_Y - 16 + ((SIN(orig.x) / 32) | 0);
  ob.dx = ob.dy = 0;
  ob.orient = 0;
  ob.symbol = getSymset(g.symbols, 'swballoonsym', 0).sym[0];
  ob.movef = moveballoon;
  ob.faction = orig.faction;
  ob.clr = ob.faction;
  ob.onmap = true;
  addPlayerTarget(g, ob, orig);
  return ob;
}

export function moveballoon(g: Game, ob: Obj): boolean {
  const orig = ob.originalOb!;
  if (ob.life === -1) {
    g.world.deallobj(ob);
    return false;
  }
  // The spotter takes the odd pot-shot, but never upward.
  if (ob.state === ObjState.FLYING && g.gamenum > 0 && g.countmove % 7 === 0) {
    const plane = findEnemyPlane(g, ob);
    if (plane && plane.y < ob.y) {
      initshot(g, ob, plane);
      ob.firing = plane;
    }
  }
  // Drift on a sine walk keyed by X so neighbouring balloons differ.
  const step = g.countmove + orig.x;
  const dx = SIN((step / 7) | 0) * 128;
  const dy = SIN((step / 3) | 0) * 128;
  ob.dx = dx >> 16;
  ob.ldx = dx & 0xffff;
  ob.dy = dy >> 16;
  ob.ldy = dy & 0xffff;
  g.world.movexy(ob, g.level.maxX, xy);
  const f = orig.orient * 3 + (dx >= 20000 ? 2 : dx <= -20000 ? 0 : 1);
  ob.symbol = getSymset(g.symbols, 'swballoonsym', f).sym[orig.transform];
  return true;
}

export function initox(g: Game, orig: OriginalOb): Obj | null {
  if (g.playmode === PlayMode.NOVICE || !g.conf.animals) {
    return null;
  }
  const ob = g.world.allocobj();
  ob.type = ObjType.OX;
  ob.state = ObjState.STANDING;
  ob.x = orig.x;
  ob.y = g.ground[ob.x] + 16;
  ob.orient = ob.lx = ob.ly = ob.ldx = ob.ldy = ob.dx = ob.dy = 0;
  ob.faction = Faction.NONE;
  ob.symbol = getSymset(g.symbols, 'swoxsym', 0).sym[orig.transform];
  ob.movef = moveox;
  ob.clr = 1;
  return ob;
}

export function moveox(g: Game, ob: Obj): boolean {
  const transform = ob.originalOb!.transform;
  ob.symbol = getSymset(g.symbols, 'swoxsym', ob.state !== ObjState.STANDING ? 1 : 0).sym[
    transform
  ];
  return true;
}
